import { Actions, ofType } from '@ngrx/effects';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  DataObject,
  ExportSchedule,
  NomPushServiceType,
  OutputSettingsType,
} from '../../../models/core.models';
import {
  EditorFormEntity,
  FormMode,
  FormProperty,
} from 'src/app/core/models/form.models';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormsService, minArrayLength } from '../../../services/forms.service';
import {
  NomExportTransportTypes,
  NomExternalShareOption,
  NomOutputFormat,
  NomScheduleTypes,
  NomSupportedLanguages,
  NomTimezones,
  SCHEDULE_DAY_OF_MONTH_LAST,
} from '../../../constants';
import { Store, select } from '@ngrx/store';
import {
  fetchOutputSettingsRecord,
  loadOutputSettings,
  saveOutputSettingsSuccess,
} from '../../../actions/output-settings.actions';
import {
  getContentFilters,
  selectCurrentObject,
  selectDefaultTimezoneId,
  selectFeedPostsByIds,
  selectOutputSettings,
  selectOutputSettingsRecordById,
  selectUser,
  selectUserHasMattermostAccess,
} from '../../../reducers';
import {
  saveObjectExportSettingsState,
  schedule,
  share,
} from 'src/app/core/actions/object.actions';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { CompanyService } from 'src/app/core/services/company.service';
import { DialogService } from '@sui/dialog';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { EmailObjectRequest } from '../../../services/core.service';
import { FeedService } from 'src/app/core/services/feed.service';
import { GeneralDynamicFormComponent } from '../../general-dynamic-form/general-dynamic-form.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { ObjectEditorService } from '../../../services/object-editor.service';
import { OutputSettingsComponent } from '../../popups/output-settings/output-settings.component';
import { ShareMode } from '../../popups/share-dialog/share-dialog';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { ToolbarButton } from '../../../models/toolbar.models';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/core/services/user.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { objectChanged } from 'src/app/core/actions/core.actions';
import { startOfDay } from 'date-fns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const TYPE_FIELD_BY_FORMAT: Record<NomOutputFormat, string> = {
  [NomOutputFormat.DOCX]: 'output_format_type_word',
  [NomOutputFormat.CSV]: 'output_format_type_csv',
  [NomOutputFormat.PDF]: 'output_format_type_pdf',
  [NomOutputFormat.HTML]: 'output_format_type_html',
  [NomOutputFormat.MATTERMOST]: 'output_format_type_mattermost',
};

// If you already have a canonical list in your app, use that instead.
const SUPPORTED_FORMAT_IDS: NomOutputFormat[] = [
  NomOutputFormat.DOCX,
  NomOutputFormat.CSV,
  NomOutputFormat.PDF,
  NomOutputFormat.HTML,
];

@Component({
  selector: 'app-export-settings',
  templateUrl: './export-settings.component.html',
  styleUrl: './export-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExportSettingsComponent extends GeneralDynamicFormComponent {
  @Input() exportSchedule: ExportSchedule; // export schedule for editing
  @Input() objectId: string; // object id for ad-hoc export (feed or dashboard id)
  @Input() objectClassId: number; // object class id for ad-hoc export (feed or dashboard)
  @Input() postIds: string[]; // post ids for ad-hoc export if the shareMode is SINGLE or SELECTED
  @Input() shareMode: ShareMode; // * Single, Selected or Resultset mode
  @Input() formMode: FormMode = null; // if the form is in edit or create mode of expoert schedule
  @Input() shareParams: any; // additional share parameters

  @Output() exportFormatChanged: EventEmitter<NomExportTransportTypes> =
    new EventEmitter<NomExportTransportTypes>();
  @Output() exportSettingsChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() isDirtyChange = new EventEmitter<boolean>();
  @Output() isValidChange = new EventEmitter<boolean>();

  @ViewChild('dynamicForm') dynamicForm: DynamicFormComponent;

  emailParams: EmailObjectRequest;
  editorFormEntityName = EditorFormEntity.ExportForm;

  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);
  outputSettingsOptionsAsync$ = this.store.pipe(
    select(selectOutputSettings),
    map((outputSettings) =>
      outputSettings.map((os) => ({
        value: os.id,
        name: os.title,
        groupName: os.output_settings_type.name,
      })),
    ),
  );

  clippingFeeds$: Observable<any[]>;
  storyFeeds$: Observable<any[]>;

  sidebarService = inject(SidebarService);
  dialogService = inject(DialogService);
  companyService = inject(CompanyService);

  constructor(
    protected formService: FormsService,
    protected userService: UserService,
    protected translateService: TranslateService,
    protected store: Store<AppState>,
    protected feedService: FeedService,
    protected nomService: NomenclatureService,
    protected editService: ObjectEditorService,
    protected utilsService: UtilsService,
    protected logger: LoggerService,
    protected cdRef: ChangeDetectorRef,
    private actions$: Actions,
  ) {
    super(formService, store, logger);
    this.clippingFeeds$ = this.clippingFeeds();
    this.storyFeeds$ = this.storyFeedsLoader();

    this.actions$
      .pipe(
        ofType(saveOutputSettingsSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap(({ outputSettings }) => {
          this.formGroup.get('output_settings_id').setValue(outputSettings.id);
        }),
      )
      .subscribe();
  }

  /**
   * Converts an array of boolean values representing weekdays to an array of day IDs.
   *
   * @param weekdays - An array of 7 numbers (0 or 1) representing the selection state of each weekday.
   *                   The index corresponds to the day of the week (0 for Sunday, 1 for Monday, etc.).
   *                   A value of 1 indicates the day is selected, 0 indicates it's not.
   *
   * @returns An array of numbers representing the IDs of the selected days.
   *          The IDs are mapped as follows: 1 (Monday) to 6 (Saturday), and 0 (Sunday).
   *          Only the IDs of the selected days are included in the returned array.
   */
  convertToDayIds(weekdays: boolean[]) {
    const dayMapping = [1, 2, 3, 4, 5, 6, 7];

    return weekdays
      .map((isSelected, index) => (isSelected ? dayMapping[index - 1] : null)) // Map true to IDs, false to null
      .filter((day) => day !== null); // Remove null values
  }

  convertFromDayIds(dayIds: number[] = []): boolean[] {
    const dayMapping = [1, 2, 3, 4, 5, 6, 7]; // same mapping used in convertToDayIds
    return dayMapping.map((id) => dayIds.includes(id));
  }

  /**
   * Resolves which schedule type to show for a schedule loaded from the backend.
   *
   * WEEKLY did not exist before WAP-1526: a "send on Mon/Wed/Fri" schedule was stored as
   * DAILY with a partial weekday set. Those are semantically weekly, so present them as
   * WEEKLY - otherwise they'd open as DAILY with the weekday picker hidden, and saving
   * would silently widen them to all seven days.
   *
   * A DAILY schedule with all (or no) weekdays flagged is genuinely daily and stays DAILY.
   */
  private resolveScheduleTypeId(exportSchedule: ExportSchedule): number {
    if (exportSchedule.schedule_type_id !== NomScheduleTypes.DAILY) {
      return exportSchedule.schedule_type_id;
    }

    const selectedDayIds = this.convertToDayIds(
      exportSchedule.schedule_weekdays_to_send ?? [],
    );

    const isPartialWeek =
      selectedDayIds.length > 0 && selectedDayIds.length < 7;

    return isPartialWeek ? NomScheduleTypes.WEEKLY : NomScheduleTypes.DAILY;
  }

  /**
   * Resolves the weekday flags to submit for a schedule.
   *
   * WEEKLY and HOURLY take the user's picked days. DAILY and MONTHLY hide the picker,
   * so every weekday is flagged - the backend walks forward day by day and only fires
   * on flagged days, so an empty set would yield no next run at all.
   */
  private resolveWeekdaysToSend(formValue: any): boolean[] {
    const scheduleTypeId = formValue?.schedule_type_id;
    const usesWeekdayPicker =
      scheduleTypeId == NomScheduleTypes.WEEKLY ||
      scheduleTypeId == NomScheduleTypes.HOURLY;

    if (usesWeekdayPicker && formValue?.schedule_weekdays_to_send?.length) {
      return this.convertFromDayIds(formValue.schedule_weekdays_to_send);
    }

    return new Array(7).fill(true);
  }

  /**
   * Builds the day-of-month options for MONTHLY schedules: 1-31 plus a trailing
   * "Last day of the month" entry. Days beyond a given month's length are clamped
   * server-side (e.g. 31 fires on Feb 28).
   */
  private dayOfMonthOptions() {
    const days = Array.from({ length: 31 }, (_, index) => ({
      value: index + 1,
      name: `${index + 1}`,
    }));

    return [
      ...days,
      {
        value: SCHEDULE_DAY_OF_MONTH_LAST,
        name: this.translateService.instant(
          'schedule-editor.form-option.last-day-of-month',
        ),
      },
    ];
  }

  /**
   * Schedule types that fire once per day at a fixed time of day, as opposed to HOURLY
   * which fires repeatedly within a window. These share the time-of-day and coverage
   * period controls.
   */
  private isTimeOfDayScheduleType(formGroup: FormGroup): boolean {
    const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

    return (
      scheduleTypeId == NomScheduleTypes.DAILY ||
      scheduleTypeId == NomScheduleTypes.WEEKLY ||
      scheduleTypeId == NomScheduleTypes.MONTHLY
    );
  }

  ngOnInit(): void {
    this.logger.debug('ExportSettingsComponent ngOnInit');
    this.logger.debug('ExportSchedule:', this.exportSchedule);
    this.logger.debug('ObjectId:', this.objectId);
    this.logger.debug('ObjectClassId:', this.objectClassId);
    this.logger.debug('PostIds:', this.postIds);
    this.logger.debug('ShareMode:', this.shareMode);
    this.logger.debug('FormMode:', this.formMode);

    const scheduleMode = this.formMode !== null;
    this.setupForm(scheduleMode);

    // ! do not move super.ngOnInit() up, it will break the form
    super.ngOnInit();

    if (this.exportSchedule) {
      // ! loadCurrentObjectSettings loads the output settings for the current object
      // load output settings for input object
      if (this.objectId && this.objectClassId) {
        // load context output settings
        this.store.dispatch(
          loadOutputSettings({
            objectId: this.objectId,
            objectClassId: this.objectClassId,
          }),
        );
      }

      this.populateFormValuesFromExportShedule();
    } else {
      this.loadCurrentObjectSettings();
    }

    this.estimateAndEmitFormButtons();
    this.estimateMattermostVisibility();
    this.estimateStoryVisibility();

    if (scheduleMode && this.formMode === FormMode.Create) {
      this.estimateDefaultTimezone();
    }
    this.dynamicForm?.estimateFormGroupsVisibility();

    if (
      this.shareMode === ShareMode.RESULTSET ||
      this.shareMode === undefined
    ) {
      this.hideShareToFeedButton();
    }

    this.isValidChange.emit(this.formGroup.valid);

    this.formGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.isValidChange.emit(this.formGroup.valid);

          this.exportSettingsChanged.emit(this.getFormValues());
        }),
      )
      .subscribe();

    this.outputSettingsOptionsAsync$
      .pipe(
        tap((options) => {
          const formProperty = this.formProperties.find(
            (p) => p.propertyName === 'output_settings_id',
          );

          if (formProperty) {
            formProperty.loading = options.length === 0;
            formProperty.additionalConfig['buttons'].forEach((button) => {
              button.visible = options.length > 0;
            });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    // downlaod settings are per feed or dashobard

    this.formPropertiesSubject
      .pipe(
        tap(() => {
          // this should trigger dynamicFormNgChanges end estimate the visibility of the form fields and gruops
          this.dynamicForm.estimateFormGroupsVisibility();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.formGroup
      .get('export_transport_type')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        withLatestFrom(this.store.select(selectCurrentObject)),
        tap(([transportType, object]) => {
          if (object) {
            const isDownloadSettings =
              !this.shareMode || this.shareMode === ShareMode.RESULTSET;

            const objectKey = isDownloadSettings
              ? OutputSettingsType.DOWNLOAD_SETTINGS
              : OutputSettingsType.SHARE_SETTINGS;

            this.saveObjectExportSettingsState(object, objectKey);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.formGroup
      .get('mattermost_server_id')
      ?.valueChanges.pipe(
        tap(() => {
          //this.logger.debug('Mattermost server changed, clearing channels');
          (this.formGroup.get('channels') as FormArray)?.clear();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(objectChanged),
        tap((action) => {
          if (!action?.object) return;

          this.loadCurrentObjectSettings();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private estimateAndEmitFormButtons() {
    const transport$ = this.formGroup
      .get('export_transport_type')!
      .valueChanges.pipe(
        startWith(this.formGroup.get('export_transport_type')?.value),
      );
    transport$
      .pipe(
        tap((exportTransportTypeId) => {
          this.updateButtons();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private populateFormValuesFromExportShedule() {
    const mappedSchedule = {
      ...this.exportSchedule,
      email_recipients: this.exportSchedule.emails,
      email_subject: this.exportSchedule.subject,
      // only include formats where format.selected is true
      formats: this.exportSchedule.formats
        .filter((format) => format.selected)
        .map((format) => format.id),

      // only include languages where language.selected is true
      languages: this.exportSchedule.languages
        .filter((language) => language.selected)
        .map((language) => language.id),

      output_settings_id: this.exportSchedule.output_settings_id,
      schedule_description: this.exportSchedule.description,

      // Legacy DAILY schedules with a partial weekday set are really weekly - see
      // resolveScheduleTypeId(). Must be mapped, or saving would widen them to all 7 days.
      schedule_type_id: this.resolveScheduleTypeId(this.exportSchedule),

      schedule_weekdays_to_send: this.exportSchedule.schedule_weekdays_to_send
        ? this.convertToDayIds(this.exportSchedule.schedule_weekdays_to_send)
        : [],

      // 0 is the "last day of the month" sentinel, so only fall back when truly absent
      schedule_day_of_month_to_send:
        this.exportSchedule.schedule_day_of_month_to_send ?? 1,

      schedule_start_date: this.utilsService.unixToDate(
        this.exportSchedule.schedule_start_date,
      ),
      schedule_end_date: this.exportSchedule.schedule_end_date
        ? this.utilsService.unixToDate(this.exportSchedule.schedule_end_date)
        : null, // this is optional field
      schedule_time_of_day_to_send: this.utilsService.timeStringToDate(
        this.exportSchedule.schedule_time_of_day_to_send,
      ),
      recurring_period_start_time: this.utilsService.timeStringToDate(
        this.exportSchedule.recurring_period_start_time,
      ),
      recurring_period_end_time: this.utilsService.timeStringToDate(
        this.exportSchedule.recurring_period_end_time,
      ),
      schedule_cover_period_end_time: this.utilsService.timeStringToDate(
        this.exportSchedule.schedule_cover_period_end_time,
      ),

      output_format_type_word: this.exportSchedule.formats.find(
        (format) => format.id === NomOutputFormat.DOCX,
      ).output_type_id,
      output_format_type_csv: this.exportSchedule.formats.find(
        (format) => format.id === NomOutputFormat.CSV,
      ).output_type_id,
      output_format_type_pdf: this.exportSchedule.formats.find(
        (format) => format.id === NomOutputFormat.PDF,
      ).output_type_id,
      output_format_type_html: this.exportSchedule.formats.find(
        (format) => format.id === NomOutputFormat.HTML,
      ).output_type_id,
    };
    this.logger.debug('Mapped schedule:', mappedSchedule);
    this.formGroup.addControl(
      'export_schedule_id',
      new FormControl(this.exportSchedule.id),
    );

    this.formService.populateFormValues(
      this.formGroup,
      mappedSchedule,
      null,
      false,
    );

    this.formProperties.forEach((property) => {
      if (property.propertyName === 'schedule_start_date') {
        property.additionalConfig['minDate'] = this.utilsService.unixToDate(
          this.exportSchedule.schedule_start_date,
        );
      }
    });

    this.logger.debug('Form values after population:', this.formGroup.value);
  }

  private estimateDefaultTimezone() {
    this.store
      .select(selectDefaultTimezoneId)
      .pipe(
        take(1),
        tap((timezoneId) => {
          if (timezoneId && this.formGroup.contains('timezone_id')) {
            this.formGroup.get('timezone_id')!.setValue(timezoneId);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private estimateMattermostVisibility() {
    this.store
      .select(selectUserHasMattermostAccess)
      .pipe(
        // only care about the first `false` value
        filter((has) => has === false),
        take(1),
        tap(() => this.hideMattermostButton()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadCurrentObjectSettings() {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        tap((object) => {
          if (!object) {
            // no current object → reset or disable form
            this.formGroup.reset();
            return;
          }
          // if we have current object, load output settings
          this.store.dispatch(
            loadOutputSettings({
              objectId: object.object_id,
              objectClassId: object.object_class_id,
            }),
          );

          // decide which settings to pull
          const objectKey =
            this.shareMode === ShareMode.RESULTSET ||
            this.shareMode === undefined
              ? OutputSettingsType.DOWNLOAD_SETTINGS
              : OutputSettingsType.SHARE_SETTINGS;

          const rawSettings = object[objectKey];
          if (rawSettings) {
            // normalize languages & formats
            const exportSettings = {
              ...rawSettings,
              languages: Array.isArray(rawSettings.languages)
                ? rawSettings.languages
                    .map((lang) => Number(lang))
                    .filter((n) => !isNaN(n))
                : rawSettings.languages != null
                  ? [Number(rawSettings.languages)].filter((n) => !isNaN(n))
                  : [],
              formats: Array.isArray(rawSettings.formats)
                ? rawSettings.formats
                    .map((format) => Number(format))
                    .filter((n) => !isNaN(n))
                : rawSettings.formats != null
                  ? [Number(rawSettings.formats)].filter((n) => !isNaN(n))
                  : [],
            };

            // do not populate languages when export transport type do not support it
            if (
              this.formGroup?.get(['export_transport_type'])?.value !==
                NomExportTransportTypes.FILE &&
              this.formGroup?.get(['export_transport_type'])?.value !==
                NomExportTransportTypes.EMAIL
            ) {
              delete exportSettings['languages'];
            }

            this.formService.populateFormValues(this.formGroup, exportSettings);
          }
        }),
        // if object was falsy, we stop here
        filter((object): object is DataObject => !!object),
        switchMap((object) => {
          const objectKey =
            this.shareMode === ShareMode.RESULTSET ||
            this.shareMode === undefined
              ? OutputSettingsType.DOWNLOAD_SETTINGS
              : OutputSettingsType.SHARE_SETTINGS;

          const fieldsToMonitor = [
            'formats',
            'languages',
            'output_settings_id',
            'email_recipients',
          ];

          const immediateChanges$ = merge(
            ...fieldsToMonitor.map(
              (field) => this.formGroup.get(field)!.valueChanges,
            ),
          );

          const emailSubjectChanges$ = this.formGroup
            .get('email_subject')!
            .valueChanges.pipe(debounceTime(500), distinctUntilChanged());

          const changes$ = merge(immediateChanges$, emailSubjectChanges$);

          return changes$.pipe(
            tap(() => {
              this.saveObjectExportSettingsState(object, objectKey);
            }),
            takeUntilDestroyed(this.destroyRef),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private saveObjectExportSettingsState(
    object: DataObject,
    objectKey: OutputSettingsType,
  ) {
    const commonValues = {
      formats: this.formGroup.get('formats')!.value,
      languages: this.formGroup.get('languages')!.value,
      output_settings_id: this.formGroup.get('output_settings_id')!.value,
      email_subject: this.formGroup.get('email_subject')!.value,
      email_recipients: this.formGroup.get('email_recipients')!.value,
      export_transport_type: this.formGroup.get('export_transport_type')!.value,
    };
    this.store.dispatch(
      saveObjectExportSettingsState({
        contextObject: object,
        settingsType: objectKey,
        settings: commonValues,
      }),
    );
  }

  private hideMattermostButton() {
    this.formProperties.forEach((property) => {
      if (property.propertyName === 'export_transport_type') {
        /// remove property additionalConfig property buttons button key mattermost
        property?.additionalConfig['buttons']?.forEach((button) => {
          if (button.value === NomExportTransportTypes.MATTERMOST) {
            button.visible = false;
          }
        });
      }
    });
  }

  private hideShareToFeedButton() {
    this.formProperties.forEach((property) => {
      if (property.propertyName === 'export_transport_type') {
        /// remove property additionalConfig property buttons button key mattermost
        property?.additionalConfig['buttons']?.forEach((button) => {
          if (
            button.value === NomExportTransportTypes.FEED ||
            button.value === NomExportTransportTypes.STORY
          ) {
            button.visible = false;
          }
        });
      }
    });
  }

  private hideStoryButton() {
    this.formProperties.forEach((property) => {
      if (property.propertyName === 'export_transport_type') {
        property?.additionalConfig['buttons']?.forEach((button) => {
          if (button.value === NomExportTransportTypes.STORY) {
            button.visible = false;
          }
        });
      }
    });
  }

  private estimateStoryVisibility() {
    this.store
      .pipe(
        select(selectUser),
        filter(Boolean),
        take(1),
        tap((user) => {
          if (!user.feature_components?.[AppFeatures.STORIES_ACCESS]) {
            this.hideStoryButton();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private clippingFeeds = () => {
    return this.userService.fetchUserClippingFeeds().pipe(
      map((feeds) => {
        const sortedFeeds = feeds
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title));
        return this.formService.transformForSelect(
          sortedFeeds,
          (feed) => feed.id,
          (feed) => feed.title,
          null,
        );
      }),
      shareReplay(1),
    );
  };

  private storyFeedsLoader = () => {
    return this.companyService.fetchCompanyStories().pipe(
      map((stories) => {
        const sortedStories = stories
          .slice()
          .sort((a, b) => a.title.localeCompare(b.title));
        return this.formService.transformForSelect(
          sortedStories,
          (story) => story.post_id,
          (story) => story.title,
          null,
        );
      }),
      shareReplay(1),
    );
  };

  updateButtons() {
    const transport = this.formGroup.get('export_transport_type')!.value;
    const isScheduleMode = this.formMode !== null;
    const translate = this.translateService.instant.bind(this.translateService);
    const action = this.submit.bind(this);

    this.logger.debug('updateButtons -> exportTransrportType', transport);

    // Define the minimal data for each button
    const defs: Array<{
      key: string; // translation key suffix
      icon: [string, string]; // FontAwesome icon tuple
      displayWhen: () => boolean; // predicate for shouldDisplay
    }> = [
      {
        key: 'download',
        icon: ['fas', 'download'],
        displayWhen: () => transport === NomExportTransportTypes.FILE,
      },
      {
        key: 'email',
        icon: ['fas', 'envelope'],
        displayWhen: () =>
          !isScheduleMode && transport === NomExportTransportTypes.EMAIL,
      },
      {
        key: 'share-to-feed',
        icon: ['fas', 'share'],
        displayWhen: () =>
          !isScheduleMode && transport === NomExportTransportTypes.FEED,
      },
      {
        key: 'send',
        icon: ['fas', 'paper-plane'],
        displayWhen: () =>
          !isScheduleMode && transport === NomExportTransportTypes.MATTERMOST,
      },
      {
        key: 'configure-schedule',
        icon: ['fas', 'calendar'],
        displayWhen: () => isScheduleMode,
      },
    ];

    // Map to full ToolbarButton objects
    const buttons: ToolbarButton[] = defs.map((d) => ({
      type: 'button',
      variant: 'primary',
      label: translate(`export-settings.button.${d.key}`),
      action,
      shouldDisplay: d.displayWhen(),
      leadingIcon: d.icon as IconProp,
    }));

    this.buttons$.next(buttons);
  }

  /**
   * Edits the output settings for a given outputSettingsId.
   * @param outputSettingsId - The ID of the output settings to edit.
   */
  editOutputSettings(outputSettingsId: number) {
    if (this.objectId && this.objectClassId) {
      this.editService.editOutputSettings(
        outputSettingsId,
        this.objectId,
        this.objectClassId,
      );
    }
  }

  /**
   * Clones the output settings identified by the given outputSettingsId.
   */
  cloneOutputSettings(outputSettingsId: number): void {
    this.store
      .select(selectCurrentObject)
      .pipe(
        take(1),
        tap((currentObject) => {
          this.store.dispatch(
            fetchOutputSettingsRecord({
              outputSettingsId,
              objectId: currentObject.id,
              objectClassId: currentObject.object_class_id,
            }),
          );
        }),
        switchMap(() =>
          this.store.pipe(
            select(selectOutputSettingsRecordById(outputSettingsId)),
            filter((outputSettings) => !!outputSettings),
            withLatestFrom(this.store.select(selectCurrentObject)),
            take(1),
          ),
        ),
        tap(([outputSettings, currentObject]) => {
          const outputSettingsClone = {
            ...outputSettings,
            id: null,
            title: outputSettings?.title + ' (Copy)',
          };

          const dialogRef = this.dialogService.openTabsDialog(
            OutputSettingsComponent,
            {
              data: {
                outputSettings: outputSettingsClone,
                objectId: currentObject.id,
                objectClassId: currentObject.object_class_id,
              },
              closeOnClick: false,
              closeOnEscape: true,
              width: '75%',
              height: '75%',
              minWidth: 1440,
              minHeight: 810,
              maxWidth: 1600,
              maxHeight: 900,
            },
          );

          dialogRef.closed.subscribe((result) => {
            console.log('Dialog closed with result:', result);
          });
        }),
      )
      .subscribe();
  }

  onSubmitSuccess(formValue: any): void {
    this.logger.debug('Form value:', formValue);

    const seedFormats: Array<{ id: NomOutputFormat; [k: string]: any }> = this
      .exportSchedule?.formats?.length
      ? this.exportSchedule.formats
      : SUPPORTED_FORMAT_IDS.map((id) => ({ id }));

    const getOutputTypeIdFromForm = (id: NomOutputFormat) => {
      const field = TYPE_FIELD_BY_FORMAT[id];
      return field ? (formValue?.[field] ?? null) : null;
    };

    const updatedFormats =
      seedFormats.map((fmt) => {
        const id = fmt.id as NomOutputFormat;
        return {
          ...fmt,
          selected: !!formValue?.formats?.some(
            (fid: NomOutputFormat) => fid === id,
          ),
          output_type_id:
            getOutputTypeIdFromForm(id) ?? fmt['output_type_id'] ?? null,
        };
      }) ?? null;

    combineLatest([
      this.store.select(getContentFilters),
      this.store.select(selectCurrentObject),
    ])
      .pipe(take(1))
      .subscribe(([contentFilters, object]) => {
        if (!contentFilters) {
          this.logger.debug('No content object found');
          return;
        }

        const objectId = this.objectId ?? object?.object_id ?? null;
        const objectClassId =
          this.objectClassId ?? object?.object_class_id ?? null;

        // Base payload shared between schedule/share
        const baseSettings = {
          ...formValue,
          share_mode: this.shareMode,
          ...(updatedFormats ? { formats: updatedFormats } : {}),
        };

        if (this.formMode !== null) {
          // SCHEDULE path
          const settings = {
            ...baseSettings,
            // Form holds native Date; backend submit API expects the structured
            // shapes ({year,month,day} / {hour,minute}) and converts them
            // server-side. (Backend RETURNS unix / "HH:mm" — handled on read.)
            schedule_start_date: this.utilsService.dateToYmd(
              formValue?.schedule_start_date,
            ),
            schedule_end_date: this.utilsService.dateToYmd(
              formValue?.schedule_end_date,
            ),
            schedule_time_of_day_to_send: this.utilsService.dateToHm(
              formValue?.schedule_time_of_day_to_send,
            ),
            recurring_period_start_time: this.utilsService.dateToHm(
              formValue?.recurring_period_start_time,
            ),
            recurring_period_end_time: this.utilsService.dateToHm(
              formValue?.recurring_period_end_time,
            ),
            schedule_cover_period_end_time: this.utilsService.dateToHm(
              formValue?.schedule_cover_period_end_time,
            ),
            // DAILY and MONTHLY hide the weekday picker, so send every weekday: the
            // backend walks days forward and only fires on flagged ones, and an empty
            // set would never produce a next run.
            schedule_weekdays_to_send: this.resolveWeekdaysToSend(formValue),
          };

          this.store.dispatch(
            schedule({
              objectId,
              objectClassId,
              postIds: this.postIds,
              contentFilters,
              settings,
              formMode: this.formMode,
            }),
          );
        } else {
          // SHARE (one-off export) path
          const settings = { ...baseSettings };

          this.store.dispatch(
            share({
              exportTransportId: settings.export_transport_type,
              objectId,
              objectClassId,
              postIds: this.postIds,
              contentFilters,
              settings,
              shareParams: this.shareParams,
            }),
          );

          if (settings.export_transport_type === NomExportTransportTypes.FILE) {
            this.sidebarService.closeSidebar();
          }
        }
      });
  }

  getFormValues() {
    return this.formGroup.value;
  }

  setupForm(scheduleMode: boolean = false) {
    const emailSuggestion = () => {
      return this.store.select(selectUser).pipe(
        switchMap((user) =>
          this.userService.fetchEmailRecipentsSuggestions(user.id),
        ),
        map((recipients) =>
          recipients.map((recipient) => {
            // Transform each recipient to a new structure here
            return {
              text: recipient.text,
              id: recipient.text,
            };
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      );
    };

    const languageOptions = this.formService.transformForSelect(
      NomSupportedLanguages,
      (language) => language.id,
      (language) => language.name,
      null,
    );

    this.formProperties = [
      {
        propertyGroup: {
          key: 'export-settings.group.general-settings',
          style: 'general-settings',
        },
        propertyName: 'title',
        propertyLabel: this.translateService.instant(
          'export-settings.label.schedule-title',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'schedule-editor.placeholder.schedule-title',
          ),
          //validators: [this.validateEmail]
        },
        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },
        validateBy: (formGroup) => {
          return scheduleMode ? [Validators.required] : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.general-settings',
          style: 'general-settings',
        },
        propertyName: 'schedule_description',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.description',
        ),
        controlType: 'textarea',
        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },
        additionalConfig: {
          placeholder: this.translateService.instant(
            'schedule-editor.input-description.placeholder',
          ),
          rows: 2,
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'export_transport_type',
        propertyLabel: this.translateService.instant(
          'export-settings.label.select-export-type',
        ),
        controlType: 'toggle-button-group',
        defaultValue: NomExportTransportTypes.FILE,
        formChanger: true,

        validators: [Validators.required],
        shouldDisplay: (formGroup) => {
          return !scheduleMode;
        },
        additionalConfig: {
          onToggleButtonsChange: (value) => {
            const formatsArray = this.formGroup.get('formats') as FormArray;
            // remove any old controls
            formatsArray.clear();

            // conditionally add a new one
            if (value === NomExportTransportTypes.MATTERMOST) {
              formatsArray.push(new FormControl(NomOutputFormat.MATTERMOST));
            } else {
              // remove MATTERMOST if it was selected
              const index = formatsArray.controls.findIndex(
                (control) => control.value === NomOutputFormat.MATTERMOST,
              );
              if (index !== -1) {
                formatsArray.removeAt(index);
              }
            }
            this.exportFormatChanged.emit(value);
          },
          buttons: [
            {
              label: this.translateService.instant(
                'export-settings.button.file',
              ),
              value: NomExportTransportTypes.FILE,
              icon: ['fas', 'note-sticky'],
              background: '#00AEEA',
              visible: true,
            },
            {
              label: this.translateService.instant(
                'export-settings.button.email',
              ),
              value: NomExportTransportTypes.EMAIL,
              icon: ['fas', 'envelope'],
              background: '#FFB400',
            },
            {
              label: this.translateService.instant(
                'export-settings.button.mattermost',
              ),
              value: NomExportTransportTypes.MATTERMOST,
              icon: ['fas', 'turn-up'], // registered custom icon name in shared-module, can not have custom name
              background: '#0072C6',
            },
            {
              label: this.translateService.instant(
                'export-settings.button.feed',
              ),
              value: NomExportTransportTypes.FEED,
              icon: ['fas', 'table-list'],
              background: 'var(--color-product-clippingfeed)',
            },
            {
              label: this.translateService.instant(
                'export-settings.button.story',
              ),
              value: NomExportTransportTypes.STORY,
              icon: ['fas', 'book'],
              background: 'var(--storyfeed)',
            },
          ],
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'message',
        controlType: 'chat-message',
        propertyLabel: this.translateService.instant(
          'export-settings.label.annotation',
        ),
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.MATTERMOST
          );
        },
        validateBy: (formGroup) => {
          return [];
        },
        additionalConfig: {},
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'formats',
        controlType: 'checkbox-array',
        propertyLabel: this.translateService.instant(
          'export-settings.label.select-format',
        ),
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value !=
            NomExportTransportTypes.FEED
          );
        },
        formChanger: true, //
        validateBy: (formGroup: FormGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.EMAIL ||
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.FILE ||
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.MATTERMOST ||
            scheduleMode
            ? [minArrayLength(1)]
            : [];
        },
        additionalConfig: {
          values: this.formService.scheduleOutputFormats,
          filterFn: (formGroup: FormGroup) => {
            const transportType = formGroup.get('export_transport_type')?.value;

            return this.formService.scheduleOutputFormats.filter((format) => {
              // if it's HTML, only show it for EMAIL
              if (
                format.value == NomOutputFormat.HTML &&
                transportType != NomExportTransportTypes.EMAIL &&
                !scheduleMode
              ) {
                return false;
              }

              // if it's MATTERMOST, only show it for MATTERMOST
              if (
                format.value == NomOutputFormat.MATTERMOST &&
                transportType !== NomExportTransportTypes.MATTERMOST
              ) {
                return false;
              }

              // everything else is fine
              return true;
            });
          },
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'output_format_type_word',
        controlType: 'select',
        propertyLabel: this.translateService.instant(
          'export-settings.label.output-format-types-word',
        ),
        valueOptions: this.formService.formatOutputTypes,
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['formats'])?.value?.includes(NomOutputFormat.DOCX)
          );
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'output_format_type_csv',
        controlType: 'select',
        propertyLabel: this.translateService.instant(
          'export-settings.label.output-format-types-csv',
        ),
        valueOptions: this.formService.formatOutputTypes,
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['formats'])?.value?.includes(NomOutputFormat.CSV)
          );
        },
      },
      ,
      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'output_format_type_html',
        controlType: 'select',
        propertyLabel: this.translateService.instant(
          'export-settings.label.output-format-types-html',
        ),
        valueOptions: this.formService.formatOutputTypes,
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['formats'])?.value?.includes(NomOutputFormat.HTML)
          );
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'output_format_type_pdf',
        controlType: 'select',
        propertyLabel: this.translateService.instant(
          'export-settings.label.output-format-types-pdf',
        ),
        valueOptions: this.formService.formatOutputTypes,
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['formats'])?.value?.includes(NomOutputFormat.PDF)
          );
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'languages',
        propertyLabel: this.translateService.instant(
          'export-settings.label.select-language',
        ),
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.FILE ||
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.EMAIL
          );
        },
        controlType: 'checkbox-array',
        validators: [minArrayLength(1)],

        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.EMAIL ||
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.FILE
            ? [minArrayLength(1)]
            : [];
        },
        additionalConfig: {
          values: languageOptions,
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'language_id',
        propertyLabel: this.translateService.instant(
          'export-settings.label.select-language',
        ),
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.MATTERMOST &&
            this.shareMode != ShareMode.RESULTSET &&
            this.shareMode != undefined // sidebar do not pass params currently and this is undefined
          );
        },
        controlType: 'select',

        valueOptionsAsync: this.nomService.fetchLanguages(true).pipe(
          map((languages) => {
            const sortedLanguages = [...languages].sort((a, b) =>
              a.name.localeCompare(b.name),
            );

            const options = this.formService.transformForSelect(
              sortedLanguages,
              (lang) => lang.id,
              (lang) => lang.name,
              null,
            );

            // Add "Original language" option at the beginning
            const originalLangOption = {
              value: 0,
              name: this.translateService.instant(
                'forms.form-option.original-post-language',
              ),
            };

            return [originalLangOption, ...options];
          }),
        ),
        defaultValue: 0,
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'output_settings_id',
        controlType: 'select',
        propertyLabel: this.translateService.instant(
          'export-settings.label.select-output-settings',
        ),
        valueOptionsAsync: this.outputSettingsOptionsAsync$,
        shouldDisplay: (formGroup: FormGroup): boolean => {
          const type = formGroup.get('export_transport_type')
            ?.value as NomExportTransportTypes;
          // pull raw array (could be [1, 2] or ['1','2'] or mixed)
          const raw = formGroup.get('formats')?.value as Array<number> | null;
          // coerce everything to numbers
          const formats: number[] = (raw || []).map((f) => +f);

          // Always show for EMAIL or FILE
          if (
            type === NomExportTransportTypes.EMAIL ||
            type === NomExportTransportTypes.FILE
          ) {
            return true;
          }

          // For Mattermost only if DOCX or CSV or PDF is selected
          if (type === NomExportTransportTypes.MATTERMOST) {
            return (
              formats.includes(NomOutputFormat.DOCX) ||
              formats.includes(NomOutputFormat.CSV) ||
              formats.includes(NomOutputFormat.PDF)
            );
          }
          return false;
        },
        additionalConfig: {
          groupBy: 'groupName',
          buttons: [
            {
              leadingIcon: ['fas', 'gear'],
              variant: 'primary',
              visible: true,
              enabled: true,
              action: this.editOutputSettings.bind(this),
              tooltip: this.translateService.instant(
                'export-settings.tooltip.edit-output-settings',
              ),
            },
          ],
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.export-settings',
          style: 'export-settings',
        },
        propertyName: 'export_file_name',
        controlType: 'text',
        propertyLabel: this.translateService.instant(
          'export-settings.label.export-file-name',
        ),
        valueOptionsAsync: this.outputSettingsOptionsAsync$,
        shouldDisplay: (formGroup: FormGroup): boolean => {
          const type = formGroup.get('export_transport_type')
            ?.value as NomExportTransportTypes;
          // pull raw array (could be [1, 2] or ['1','2'] or mixed)
          const raw = formGroup.get('formats')?.value as Array<number> | null;
          // coerce everything to numbers
          const formats: number[] = (raw || []).map((f) => +f);

          // Show for relevant transport types, and if DOCX or CSV or PDF format is selected
          if (
            type === NomExportTransportTypes.EMAIL ||
            type === NomExportTransportTypes.FILE ||
            type === NomExportTransportTypes.MATTERMOST
          ) {
            return (
              formats.includes(NomOutputFormat.DOCX) ||
              formats.includes(NomOutputFormat.CSV) ||
              formats.includes(NomOutputFormat.PDF)
            );
          }
          return false;
        },

        shouldDisplayAsync: (formGroup) => {
          // if the share mode is not SINGLE, we immediately return false
          if (this.shareMode !== ShareMode.SINGLE) {
            return of(false);
          }

          if (!this.postIds || this.postIds.length === 0) {
            // If no post IDs are provided, we cannot determine if there's a video attachment.
            return of(false);
          }

          // select the post by id from the store and check for a video attachment
          return this.store.select(selectFeedPostsByIds(this.postIds)).pipe(
            take(1), // only get the first emitted value
            map((posts) => {
              // Determine if the post has a video attachment.
              // Adjust the logic below according to your post structure.
              const post = posts[0];
              const hasVideoAttachment =
                post &&
                post.attachments &&
                post.attachments.some((att) => att.type === 'video');

              // Only display if there's a video and the export transport type is MATTERMOST.
              return hasVideoAttachment;
            }),
          );
        },
      },

      // transport settings
      {
        propertyGroup: {
          key: 'export-settings.group.delivery-channels',
          style: 'delivery-channels',
        },
        propertyName: 'notification_push_service',
        controlType: 'checkbox-array',
        propertyLabel: this.translateService.instant(
          'export-settings.label.schedule-select-delivery-channels',
        ),
        formChanger: true,
        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },
        validateBy: (formGroup) => {
          return scheduleMode ? [Validators.required] : [];
        },
        defaultValue: [NomPushServiceType.EMAIL],
        additionalConfig: {
          values: [
            {
              value: NomPushServiceType.EMAIL,
              name: this.translateService.instant(
                'alert-editor.label.email-notification',
              ),
              icon: ['fas', 'envelope'],
            },
            {
              value: NomPushServiceType.MATTERMOST,
              name: this.translateService.instant(
                'alert-editor.label.mattermost-notification',
              ),
              icon: ['fas', 'turn-up'], // custom mattermost icon
            },
          ],
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.email',
          style: 'email',
        },
        propertyName: 'email_recipients',
        propertyLabel: this.translateService.instant(
          'export-settings.label.emails',
        ),

        controlType: 'tag-input',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'export-settings.placeholder.recepients',
          ),
          valueOptionsAsync: emailSuggestion(),
          displayBy: 'text',
          identifyBy: 'text',
          //validators: [this.validateEmail]
        },
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.EMAIL ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.EMAIL))
          );
        },
        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.EMAIL ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.EMAIL))
            ? [Validators.required]
            : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.email',
          style: 'email',
        },
        propertyName: 'email_subject',
        propertyLabel: this.translateService.instant(
          'export-settings.label.subject',
        ),
        controlType: 'text',
        additionalConfig: {
          placeholder: this.translateService.instant(
            'export-settings.placeholder.subject',
          ),
        },
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.EMAIL ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.EMAIL))
          );
        },
        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.EMAIL ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.EMAIL))
            ? [Validators.required]
            : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.mattermost',
          style: 'mattermost',
        },
        propertyName: 'mattermost_server_id',
        propertyLabel: this.translateService.instant(
          'export-settings.label.mattermost-server',
        ),
        controlType: 'select',
        valueOptionsAsync: this.nomService.fetchMattermostServers().pipe(
          map((servers) =>
            this.formService.transformForSelect(
              servers,
              (server) => server.id,
              (server) => server.display_name,
              null,
            ),
          ),
          tap((options) => {
            const control = this.formGroup.get('mattermost_server_id');
            if (control && !control.value && options && options.length > 0) {
              control.setValue(options[0].value);
            }
          }),
        ),

        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.MATTERMOST ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.MATTERMOST)) // if schedule mode, show Mattermost server selection if Mattermost is selected as a notification method
          );
        },

        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.MATTERMOST ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.MATTERMOST))
            ? [Validators.required]
            : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.mattermost',
          style: 'mattermost',
        },
        propertyName: 'external_share_option_id',
        propertyLabel: this.translateService.instant(
          'export-settings.label.share-options',
        ),
        controlType: 'select',
        valueOptions: this.formService.externalShareOptions,
        defaultValue: NomExternalShareOption.CONTENT_EXCERPT,
        shouldDisplay: (formGroup) => {
          return (
            (formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.MATTERMOST &&
              this.shareMode != ShareMode.RESULTSET &&
              this.shareMode != undefined) || // sidebar do not pass params currently and this is undefined
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.MATTERMOST))
          );
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.mattermost',
          style: 'mattermost',
        },
        propertyName: 'embed_video_subtitles',
        propertyLabel: this.translateService.instant(
          'export-settings.label.embed-video-subtitles',
        ),
        controlType: 'checkbox',
        additionalConfig: {
          defaultValue: false,
          label: this.translateService.instant(
            'export-settings.label.embed-video-subtitles',
          ),
        },
        shouldDisplayAsync: (formGroup) => {
          // if the share mode is not SINGLE, we immediately return false
          if (this.shareMode !== ShareMode.SINGLE) {
            return of(false);
          }

          if (!this.postIds || this.postIds.length === 0) {
            // If no post IDs are provided, we cannot determine if there's a video attachment.
            return of(false);
          }

          // select the post by id from the store and check for a video attachment
          return this.store.select(selectFeedPostsByIds(this.postIds)).pipe(
            take(1), // only get the first emitted value
            map((posts) => {
              // Determine if the post has a video attachment.
              // Adjust the logic below according to your post structure.
              const post = posts[0];
              const hasVideoAttachment =
                post &&
                post.attachments &&
                post.attachments.some((att) => att.type === 'video');

              // Only display if there's a video and the export transport type is MATTERMOST.
              return (
                hasVideoAttachment &&
                formGroup?.get('export_transport_type')?.value ===
                  NomExportTransportTypes.MATTERMOST
              );
            }),
          );
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.mattermost',
          style: 'mattermost',
        },
        propertyName: 'channels',
        propertyLabel: this.translateService.instant(
          'export-settings.label.mattermost-channels',
        ),
        controlType: 'checkbox-array',
        additionalConfig: {
          valuesAsync: (formGroup: FormGroup) => {
            // Cache the observable on the form group to prevent multiple identical subscriptions.
            if (!formGroup['channelsValueOptions$']) {
              const mattermostServerControl = formGroup.get(
                'mattermost_server_id',
              );
              formGroup['channelsValueOptions$'] =
                mattermostServerControl.valueChanges.pipe(
                  // Start with the current value so that the chain fires immediately.
                  startWith(mattermostServerControl.value),
                  // Filter out null/undefined values if needed.
                  filter((serverId) => serverId != null),
                  // Prevent duplicate successive values.
                  distinctUntilChanged(),
                  // Cancel previous requests if a new serverId comes in.
                  switchMap((serverId: number) =>
                    this.nomService.fetchMattermostChannels(serverId),
                  ),
                  // Transform the channels into select options.
                  map((channels) =>
                    this.formService.transformForSelect(
                      channels,
                      (channel) => channel.channel_id,
                      (channel) => channel.name,
                      null,
                    ),
                  ),
                  // Share the latest value with all subscribers.
                  shareReplay(1),
                );
            }
            // Return the cached observable.
            return formGroup['channelsValueOptions$'];
          },
        },
        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
              NomExportTransportTypes.MATTERMOST ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.MATTERMOST))
          );
        },

        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.MATTERMOST ||
            (scheduleMode &&
              this.formGroup
                ?.get('notification_push_service')
                ?.value?.includes(NomPushServiceType.MATTERMOST))
            ? [Validators.required, minArrayLength(1)]
            : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.schedule-type',
          style: 'schedule-type',
        },
        propertyName: 'schedule_type_id',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.schedule-type',
        ),
        controlType: 'toggle-button-group',
        formChanger: true, // only controls with this flag trigger dynamic validators and visibility estimations
        defaultValue: NomScheduleTypes.DAILY,
        additionalConfig: {
          buttons: [
            {
              label: this.translateService.instant(
                'schedule-editor.button.hourly',
              ),
              value: NomScheduleTypes.HOURLY,
              icon: ['fas', 'clock'],
            },
            {
              label: this.translateService.instant(
                'schedule-editor.button.daily',
              ),
              value: NomScheduleTypes.DAILY,
              icon: ['fas', 'calendar-day'],
            },
            {
              label: this.translateService.instant(
                'schedule-editor.button.weekly',
              ),
              value: NomScheduleTypes.WEEKLY,
              icon: ['fas', 'calendar-week'],
            },
            {
              label: this.translateService.instant(
                'schedule-editor.button.monthly',
              ),
              value: NomScheduleTypes.MONTHLY,
              icon: ['fas', 'calendar-days'],
            },
          ],
        },

        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },
        validateBy: (formGroup) => {
          return scheduleMode ? [Validators.required] : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.data-period-settings',
          style: 'data-period-settings',
        },
        propertyName: 'schedule_cover_period',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.coverage-period',
        ),
        controlType: 'select',
        propertyGroupRow: 2,
        defaultValue: 1, // @todo: set default value as constant
        valueOptionsAsync: this.nomService.fetchScheduleCoveragePeriods().pipe(
          map((periods) => {
            return this.formService.transformForSelect(
              periods,
              (period) => period.key,
              (period) => period.title,
            );
          }),
        ),
        shouldDisplay: (formGroup) => {
          return scheduleMode && this.isTimeOfDayScheduleType(formGroup);
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.data-period-settings',
          style: 'data-period-settings',
        },
        propertyName: 'schedule_cover_period_end_time',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.cover-period-end-time',
        ),
        controlType: 'time-picker',
        columnIndex: 1,

        shouldDisplay: (formGroup) => {
          return scheduleMode && this.isTimeOfDayScheduleType(formGroup);
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'timezone_id',
        propertyLabel: this.translateService.instant(
          'export-settings.label.schedule-timezone',
        ),
        controlType: 'select',
        valueOptionsAsync: this.nomService.fetchTimezones().pipe(
          map((timezones) => {
            return this.formService.transformForSelect(
              timezones,
              (timezone) => timezone.id,
              (timezone) => timezone.name,
            );
          }),
        ),

        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },
        validateBy: (formGroup) => {
          return scheduleMode ? [Validators.required] : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'schedule_time_of_day_to_send',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.time-of-day-to-send',
        ),
        propertyGroupRow: 3,
        controlType: 'time-picker',
        defaultValue: this.utilsService.timeStringToDate('00:00'),

        shouldDisplay: (formGroup) => {
          return scheduleMode && this.isTimeOfDayScheduleType(formGroup);
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'recurring_period',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.send-on-every',
        ),
        controlType: 'input-number',
        defaultValue: 1,
        additionalConfig: {
          min: 1,
          max: 23,
        },
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.HOURLY
          );
        },
        validateBy: (formGroup) => {
          return formGroup?.get(['schedule_type_id'])?.value ==
            NomScheduleTypes.HOURLY
            ? [Validators.required]
            : [];
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'recurring_period_type_id',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.recurring-period-type',
        ),
        controlType: 'select',
        valueOptions: [{ value: 2, name: 'Hour' }],
        defaultValue: 2,
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.HOURLY
          );
        },
        validateBy: (formGroup) => {
          // console.log('recurring_period_type_id validateBy');
          return formGroup?.get(['schedule_type_id'])?.value ==
            NomScheduleTypes.HOURLY
            ? [Validators.required]
            : [];
        },
      },
      {
        propertyName: 'recurring_period_start_time',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.start-hour',
        ),
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        controlType: 'time-picker',
        defaultValue: this.utilsService.timeStringToDate('00:00'),
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.HOURLY
          );
        },

        validateBy: (formGroup) => {
          return formGroup?.get(['schedule_type_id'])?.value ==
            NomScheduleTypes.HOURLY
            ? [Validators.required]
            : [];
        },
      },
      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'recurring_period_end_time',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.end-hour',
        ),
        controlType: 'time-picker',
        defaultValue: this.utilsService.timeStringToDate('23:59'),
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.HOURLY
          );
        },
        validateBy: (formGroup) => {
          return formGroup?.get(['schedule_type_id'])?.value ==
            NomScheduleTypes.HOURLY
            ? [Validators.required]
            : [];
        },
      },
      {
        propertyName: 'schedule_start_date',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.start-date',
        ),
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        controlType: 'date-picker',

        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },

        validateBy: (formGroup) => {
          return scheduleMode ? [Validators.required] : [];
        },

        additionalConfig: {
          // Midnight, not `new Date()`: the picker disables a day when
          // startOfDay(cell) < minDate compared by raw timestamp, so a minDate
          // carrying a time of day makes today unselectable. The backend floors the
          // start date at today midnight, so today is a valid choice.
          minDate: startOfDay(new Date()),
        },
      },

      {
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        propertyName: 'schedule_end_date',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.end-date',
        ),
        controlType: 'date-picker',
        shouldDisplay: (formGroup) => {
          return scheduleMode;
        },

        additionalConfig: {
          minDate: this.formGroup?.get('schedule_start_date').value,
          placeholder: this.translateService.instant(
            'schedule-editor.placeholder.end-date',
          ),
        },
      },

      {
        propertyName: 'schedule_weekdays_to_send',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.send-on',
        ),
        // Raw key, not translateService.instant(...): groups are bucketed by this literal
        // string, so a translated key would split this control into its own group box.
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        controlType: 'checkbox-array',
        additionalConfig: {
          values: this.formService.weekDays,
          enableSelectAll: true,
        },
        // Only WEEKLY and HOURLY let the user narrow which weekdays fire. DAILY means every
        // day (the weekdays are filled in on submit) and MONTHLY is driven by day of month.
        shouldDisplay: (formGroup) => {
          const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

          return (
            scheduleMode &&
            (scheduleTypeId == NomScheduleTypes.WEEKLY ||
              scheduleTypeId == NomScheduleTypes.HOURLY)
          );
        },

        validateBy: (formGroup) => {
          const scheduleTypeId = formGroup?.get(['schedule_type_id'])?.value;

          return scheduleMode &&
            (scheduleTypeId == NomScheduleTypes.WEEKLY ||
              scheduleTypeId == NomScheduleTypes.HOURLY)
            ? [Validators.required, minArrayLength(1)]
            : [];
        },
      },

      {
        propertyName: 'schedule_day_of_month_to_send',
        propertyLabel: this.translateService.instant(
          'schedule-editor.label.day-of-month-to-send',
        ),
        propertyGroup: {
          key: 'export-settings.group.scheduling-options',
          style: 'scheduling-options',
        },
        controlType: 'select',
        defaultValue: 1,
        valueOptions: this.dayOfMonthOptions(),
        shouldDisplay: (formGroup) => {
          return (
            scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.MONTHLY
          );
        },
        validateBy: (formGroup) => {
          return scheduleMode &&
            formGroup?.get(['schedule_type_id'])?.value ==
              NomScheduleTypes.MONTHLY
            ? [Validators.required]
            : [];
        },
      },

      {
        propertyName: 'sharing_feeds',
        propertyLabel: this.translateService.instant(
          'export-settings.label.sharing-feeds',
        ),
        propertyGroup: {
          key: 'export-settings.group.share-to-feed',
          style: 'share-to-feed',
        },
        controlType: 'checkbox-array',
        additionalConfig: {
          valuesAsync: (formGroup: FormGroup) => this.clippingFeeds$,
        },

        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.FEED
          );
        },

        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.FEED
            ? [Validators.required, minArrayLength(1)]
            : [];
        },
      },

      {
        propertyName: 'story_ids',
        propertyLabel: this.translateService.instant(
          'export-settings.label.sharing-stories',
        ),
        propertyGroup: {
          key: 'export-settings.group.share-to-story',
          style: 'share-to-story',
        },
        controlType: 'checkbox-array',
        additionalConfig: {
          valuesAsync: (formGroup: FormGroup) => this.storyFeeds$,
        },

        shouldDisplay: (formGroup) => {
          return (
            formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.STORY
          );
        },

        validateBy: (formGroup) => {
          return formGroup?.get(['export_transport_type'])?.value ==
            NomExportTransportTypes.STORY
            ? [Validators.required, minArrayLength(1)]
            : [];
        },
      },
    ];
  } // setupForm
}
